import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { confirmDestructive } from '../../ui/dialogs';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, indexById } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Avatar, Card, ErrorBanner, Field, Loader, Pill, PrimaryButton, SectionLabel, TextButton, screen } from '../ui';

const DOW = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const MONTHS_NOM = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const toMs = (v) => (typeof v?.toMillis === 'function' ? v.toMillis() : v instanceof Date ? v.getTime() : Date.parse(v));
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hhmm = (ms) => { const d = new Date(ms); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const firstName = (name) => (name || '').trim().split(/\s+/)[0] || '?';
const locEmoji = (loc) => {
  const s = `${loc?.id || ''} ${loc?.name || ''}`.toLowerCase();
  if (/таб|tab|smoke|сигар|вейп|vape|снюс/.test(s)) return '🚬';
  if (/коф|cofe|coff|бариста|кафе|latte|эспрес/.test(s)) return '☕';
  return '🏪';
};
// ASCII only — these ids ride in an idempotency HTTP header, which cannot
// carry Cyrillic (fetch throws before sending).
const slug = (t) => (t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '').slice(0, 24) || 'x';

const RUNNING_SHIFT = ['opening', 'active', 'handover_pending', 'closing'];
const TERMINAL_SHIFT = ['cancelled', 'closed', 'force_closed', 'missed'];
// What to show for a materialized entry, given its shift's real status.
const entryPhase = (shift, plannedStartMs) => {
  const st = shift?.status;
  if (!shift || st === 'scheduled') return Date.now() >= plannedStartMs ? 'awaiting' : 'planned';
  if (RUNNING_SHIFT.includes(st)) return 'running';
  if (st === 'ready_to_open' || String(st).startsWith('wake_up')) return 'wakeup';
  if (st === 'missed') return 'missed';
  if (TERMINAL_SHIFT.includes(st)) return 'ended';
  return 'planned';
};

export default function ScheduleAdminScreen({ navigation }) {
  const { user } = useAuth();
  const schedules = useCollection('scheduleEntries');
  const users = useCollection('users');
  const locations = useCollection('locations');
  const shiftTypes = useCollection('shiftTypes');
  const shifts = useCollection('shifts');
  const client = useMemo(() => createAdminClient(user), [user]);

  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(() => ymd(new Date()));
  const [view, setView] = useState('list');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const entriesByDay = useMemo(() => {
    const map = {};
    for (const e of schedules.docs) {
      if (e.status === 'cancelled') continue;
      const key = ymd(new Date(toMs(e.plannedStartAt)));
      (map[key] = map[key] || []).push(e);
    }
    return map;
  }, [schedules.docs]);

  const upcoming = useMemo(() => {
    const out = [];
    const base = new Date(); base.setHours(0, 0, 0, 0);
    for (let i = 0; i < 21; i += 1) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      const list = (entriesByDay[ymd(d)] || []).slice().sort((a, b) => toMs(a.plannedStartAt) - toMs(b.plannedStartAt));
      if (list.length) out.push({ date: d, key: ymd(d), list });
    }
    return out;
  }, [entriesByDay]);

  const grid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const lead = (first.getDay() + 6) % 7; // Mon-first
    const days = [];
    for (let i = 0; i < lead; i += 1) days.push(null);
    const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    for (let d = 1; d <= count; d += 1) days.push(new Date(month.getFullYear(), month.getMonth(), d));
    return days;
  }, [month]);

  const usersById = indexById(users.docs);
  const locById = indexById(locations.docs);
  const typeById = indexById(shiftTypes.docs);
  const shiftById = indexById(shifts.docs);
  const dayEntries = (entriesByDay[selected] || []).sort((a, b) => toMs(a.plannedStartAt) - toMs(b.plannedStartAt));
  const shiftMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  const cancelEntry = (entry, phase) => {
    const running = phase === 'running';
    confirmDestructive(
      running ? 'Снять смену, которая идёт?' : 'Убрать смену из графика?',
      running
        ? 'Смена сейчас идёт. Она будет принудительно закрыта, невыполненные задачи отменены, в отчётах останется как «форс».'
        : 'Смена исчезнет из графика. История прошлых смен не затрагивается.',
      running ? 'Снять смену' : 'Убрать',
      async () => {
        try { await client.cancelScheduleEntry(entry.id, running ? 'Снята владельцем во время смены' : 'Убрана из графика владельцем'); } catch (e) { setError(readableMobileError(e)); }
      },
    );
  };

  if (schedules.loading && !schedules.docs.length) return <Loader />;
  const [y, m, dd] = selected.split('-').map(Number);
  const selLabel = `${dd} ${MONTHS[m - 1]}`;

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={error || schedules.error} />

        <Pressable onPress={() => navigation.navigate('ScheduleTemplate')} style={styles.tmplRow}>
          <Text style={styles.tmplText}>🗓 Шаблон недели — заполнять график автоматически</Text>
          <Text style={styles.tmplChevron}>›</Text>
        </Pressable>

        <View style={styles.viewTabs}>
          {[['list', 'Список'], ['calendar', 'Календарь']].map(([k, label]) => (
            <Pressable key={k} onPress={() => setView(k)} style={[styles.viewTab, view === k && styles.viewTabOn]}>
              <Text style={[styles.viewTabText, view === k && styles.viewTabTextOn]}>{label}</Text>
            </Pressable>
          ))}
        </View>

        {view === 'list' && (
          <View style={styles.agenda}>
            {!upcoming.length && <Text style={styles.empty}>На ближайшие 3 недели смен нет</Text>}
            {upcoming.map(({ date: d, key, list }) => (
              <View key={key} style={styles.agendaDay}>
                <Pressable onPress={() => { setSelected(key); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}>
                  <Text style={[styles.agendaDate, key === selected && styles.agendaDateSel]}>
                    {DOW[(d.getDay() + 6) % 7]} {d.getDate()} {MONTHS[d.getMonth()]}
                  </Text>
                </Pressable>
                {list.map((e) => {
                  const emp = e.currentEmployeeId ? usersById[e.currentEmployeeId] : null;
                  const phase = entryPhase(e.shiftId ? shiftById[e.shiftId] : null, toMs(e.plannedStartAt));
                  return (
                    <View key={e.id} style={styles.agendaRow}>
                      <Text style={styles.agendaEmoji}>{locEmoji(locById[e.locationId])}</Text>
                      <Text style={styles.agendaName}>{emp ? firstName(emp.fullName) : 'не назначен'}</Text>
                      {phase === 'running' && <Pill label="идёт" kind="good" />}
                      {phase === 'missed' && <Pill label="не открыли" kind="bad" />}
                      <Text style={styles.agendaMeta} numberOfLines={1}>
                        {e.position ? `${e.position} · ` : ''}{hhmm(toMs(e.plannedStartAt))}–{hhmm(toMs(e.plannedEndAt))}
                        {e.createdVia === 'self' ? ' · сам' : ''}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        )}

        {view === 'calendar' && (
          <>
            <View style={styles.monthBar}>
              <TextButton title="‹" onPress={() => shiftMonth(-1)} />
              <Text style={styles.monthText}>{MONTHS_NOM[month.getMonth()]} {month.getFullYear()}</Text>
              <TextButton title="›" onPress={() => shiftMonth(1)} />
            </View>

            <View style={styles.week}>{DOW.map((d) => <Text key={d} style={styles.dow}>{d}</Text>)}</View>
            <View style={styles.grid}>
              {grid.map((d, i) => {
                if (!d) return <View key={i} style={styles.cell} />;
                const key = ymd(d);
                const list = entriesByDay[key] || [];
                return (
                  <Pressable key={i} onPress={() => setSelected(key)} style={[styles.cell, styles.day, key === selected && styles.daySel]}>
                    <Text style={[styles.dayNum, key === selected && styles.dayNumSel]}>{d.getDate()}</Text>
                    <View style={styles.pips}>
                      {list.slice(0, 3).map((e, j) => <View key={j} style={[styles.pip, !e.currentEmployeeId && styles.pipGap]} />)}
                    </View>
                  </Pressable>
                );
              })}
            </View>
          </>
        )}

        <View style={styles.dayHead}>
          <SectionLabel>{selLabel}</SectionLabel>
          <TextButton title="+ Смена" onPress={() => setAdding(true)} />
        </View>
        {dayEntries.map((e) => {
          const emp = e.currentEmployeeId ? usersById[e.currentEmployeeId] : null;
          const replaced = e.currentEmployeeId && e.originalEmployeeId && e.currentEmployeeId !== e.originalEmployeeId;
          const phase = entryPhase(e.shiftId ? shiftById[e.shiftId] : null, toMs(e.plannedStartAt));
          const chip = { running: ['идёт', 'good'], wakeup: ['будильник', 'warn'], awaiting: ['ждём открытия', 'warn'], missed: ['не открыли', 'bad'] }[phase];
          return (
            <Card key={e.id} style={styles.entry}>
              <Avatar label={emp ? initials(emp.fullName) : '?'} kind={!emp ? 'orange' : replaced ? 'violet' : 'teal'} />
              <View style={{ flex: 1 }}>
                <Text style={styles.entryTitle}>
                  {locById[e.locationId]?.name || e.locationId} · {typeById[e.shiftTypeId]?.name || 'смена'}
                </Text>
                <Text style={styles.entryMeta}>
                  {hhmm(toMs(e.plannedStartAt))}–{hhmm(toMs(e.plannedEndAt))}
                  {emp ? ` · ${emp.fullName}` : ' · сотрудник не назначен'}
                  {e.position ? ` · ${e.position}` : ''}
                  {e.createdVia === 'self' ? ' · сам' : ''}
                  {replaced ? ' · замена' : ''}
                </Text>
              </View>
              <View style={styles.entryRight}>
                {chip && <Pill label={chip[0]} kind={chip[1]} />}
                {phase !== 'ended' && (
                  <TextButton title={phase === 'running' ? 'Снять' : 'Убрать'} kind="danger" onPress={() => cancelEntry(e, phase)} />
                )}
              </View>
            </Card>
          );
        })}
        {!dayEntries.length && <Text style={styles.empty}>На этот день смен нет</Text>}
      </ScrollView>

      <Modal visible={adding} animationType="slide" transparent onRequestClose={() => setAdding(false)}>
        <AddShiftSheet
          date={new Date(y, m - 1, dd)}
          users={users.docs}
          locations={locations.docs.filter((l) => l.active)}
          shiftTypes={shiftTypes.docs.filter((t) => t.active)}
          onClose={() => setAdding(false)}
          onSubmit={(id, data) => client.createScheduleEntry(id, data)}
        />
      </Modal>
    </View>
  );
}

const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

function AddShiftSheet({ date, users, locations, shiftTypes, onClose, onSubmit }) {
  const [f, setF] = useState({ locationId: '', employeeId: '', position: '', start: '09:00', end: '21:00' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const loc = locations.find((l) => l.id === f.locationId);
  const eligible = users.filter((u) => u.active && (u.locationIds || []).includes(f.locationId));
  const templates = loc?.shiftTemplates || [];
  const positions = loc?.positions || [];
  const shiftTypeId = loc?.defaultShiftTypeId || shiftTypes[0]?.id;

  const submit = async () => {
    if (!f.locationId || !f.employeeId) { setError('Выберите точку и сотрудника'); return; }
    if (!shiftTypeId) { setError('На точке не задан тип смены (вкладка «Точки» → «Настроить»)'); return; }
    if (positions.length && !f.position) { setError('Выберите должность'); return; }
    const mk = (t) => { const [hh, mm] = t.split(':').map(Number); const d = new Date(date); d.setHours(hh || 0, mm || 0, 0, 0); return d; };
    let start = mk(f.start); let end = mk(f.end);
    if (end <= start) end = new Date(end.getTime() + 24 * 3600 * 1000);
    setBusy(true); setError(null);
    try {
      await onSubmit(`sch-${slug(loc?.id || loc?.name)}-${date.getTime().toString(36)}-${Date.now().toString(36)}`, {
        locationId: f.locationId, shiftTypeId, employeeId: f.employeeId,
        position: f.position || null,
        plannedStartAt: start.toISOString(), plannedEndAt: end.toISOString(),
        timezone: loc?.timezone || 'Europe/Moscow',
      });
      onClose();
    } catch (e) {
      setError(e?.serverMessage || (e?.code ? `Отклонено: ${e.code}` : 'Не удалось создать смену'));
    } finally { setBusy(false); }
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.sheet}>
        <View style={styles.sheetHead}>
          <Text style={styles.sheetTitle}>Смена на {date.getDate()} {MONTHS[date.getMonth()]}</Text>
          <TextButton title="Отмена" kind="muted" onPress={onClose} />
        </View>
        <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">
          <ErrorBanner message={error} />
          <Text style={styles.pickLabel}>ТОЧКА</Text>
          <Picker items={locations} value={f.locationId} onPick={(id) => set({ locationId: id, employeeId: '', position: '' })} label={(l) => l.name} />

          {f.locationId ? (
            <>
              {templates.length > 0 && (
                <>
                  <Text style={styles.pickLabel}>ШАБЛОН ЧАСОВ</Text>
                  <View style={styles.pickWrap}>
                    {templates.map((t) => (
                      <Pressable key={t.id || t.label} onPress={() => set({ start: t.start, end: t.end })}
                        style={[styles.pickChip, f.start === t.start && f.end === t.end && styles.pickChipOn]}>
                        <Text style={[styles.pickChipText, f.start === t.start && f.end === t.end && styles.pickChipTextOn]}>{t.label} {t.start}–{t.end}</Text>
                      </Pressable>
                    ))}
                  </View>
                </>
              )}
              <View style={styles.rowFields}>
                <View style={{ flex: 1 }}><Field label="Начало" value={f.start} onChangeText={(start) => set({ start })} /></View>
                <View style={{ flex: 1 }}><Field label="Конец" value={f.end} onChangeText={(end) => set({ end })} /></View>
              </View>

              {positions.length > 0 && (
                <>
                  <Text style={styles.pickLabel}>ДОЛЖНОСТЬ</Text>
                  <Picker items={positions.map((p) => ({ id: p }))} value={f.position} onPick={(id) => set({ position: id })} label={(p) => p.id} />
                </>
              )}

              <Text style={styles.pickLabel}>СОТРУДНИК</Text>
              <Picker items={eligible} value={f.employeeId} onPick={(id) => set({ employeeId: id })} label={(u) => u.fullName} empty="На этой точке нет активных сотрудников" />
            </>
          ) : <Text style={styles.hint}>Сначала выберите точку</Text>}

          <PrimaryButton title="Поставить в график" onPress={submit} loading={busy} />
        </ScrollView>
      </View>
    </View>
  );
}

function Picker({ items, value, onPick, label, empty }) {
  if (!items.length) return <Text style={styles.hint}>{empty || 'Нет вариантов'}</Text>;
  return (
    <View style={styles.pickWrap}>
      {items.map((it) => (
        <Pressable key={it.id} onPress={() => onPick(it.id)} style={[styles.pickChip, value === it.id && styles.pickChipOn]}>
          <Text style={[styles.pickChipText, value === it.id && styles.pickChipTextOn]}>{label(it)}</Text>
        </Pressable>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  tmplRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md, marginBottom: spacing.sm },
  tmplText: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 13.5, flex: 1 },
  tmplChevron: { fontSize: 20, color: colors.textMuted },
  viewTabs: { flexDirection: 'row', gap: spacing.xs, backgroundColor: colors.surfaceCard, borderRadius: radius.sm, padding: 3, marginBottom: spacing.md },
  viewTab: { flex: 1, paddingVertical: spacing.sm, alignItems: 'center', borderRadius: radius.sm - 2 },
  viewTabOn: { backgroundColor: 'rgba(0,210,160,0.14)' },
  viewTabText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  viewTabTextOn: { color: colors.primary },
  agenda: { gap: spacing.md, marginBottom: spacing.sm },
  agendaDay: { gap: 4 },
  agendaDate: { ...typography.caption, color: colors.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  agendaDateSel: { color: colors.primary },
  agendaRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  entryRight: { alignItems: 'flex-end', gap: 6 },
  agendaEmoji: { fontSize: 16 },
  agendaName: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 13.5 },
  agendaMeta: { ...typography.caption, color: colors.textMuted, flex: 1, textAlign: 'right' },
  monthBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  monthText: { ...typography.h3, color: colors.textPrimary },
  week: { flexDirection: 'row' },
  dow: { flex: 1, textAlign: 'center', ...typography.caption, color: colors.textMuted, fontWeight: '700', textTransform: 'uppercase' },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: `${100 / 7}%`, aspectRatio: 1, padding: 2 },
  day: { alignItems: 'center', paddingTop: 5, borderRadius: radius.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border },
  daySel: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  dayNum: { ...typography.caption, color: colors.textSecondary, fontVariant: ['tabular-nums'] },
  dayNumSel: { color: colors.textPrimary, fontWeight: '700' },
  pips: { flexDirection: 'row', gap: 2, marginTop: 3 },
  pip: { width: 4, height: 4, borderRadius: 2, backgroundColor: colors.primary },
  pipGap: { backgroundColor: colors.danger },
  dayHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: spacing.sm },
  entry: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  entryTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 13.5 },
  entryMeta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  empty: { ...typography.body, color: colors.textMuted, textAlign: 'center', paddingVertical: spacing.lg },
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '90%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sheetTitle: { ...typography.h3, color: colors.textPrimary },
  rowFields: { flexDirection: 'row', gap: spacing.md },
  pickLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700', letterSpacing: 1 },
  pickWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  pickChip: { borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  pickChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  pickChipText: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  pickChipTextOn: { color: colors.textPrimary },
  hint: { ...typography.caption, color: colors.textMuted },
});
